import { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { ReportServices } from "../services/ReportServices";
import { getChartDateRangeForTimeframe } from "./doctorReportDateRange";
import downloadIcon from "../assets/download_icon.png";

const PAGE_LIMIT = 10;

function formatGridDateTime(iso) {
  if (!iso) return "-";

  const d = new Date(iso);

  const time = d.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const date = d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return `${time}\n${date}`;
}

function formatDurationSeconds(totalSeconds) {
  const n = Math.max(0, Math.floor(Number(totalSeconds) || 0));

  const d = Math.floor(n / 86400);
  const h = Math.floor((n % 86400) / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = n % 60;

  const parts = [];

  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (!parts.length) parts.push(`${s}s`);

  return parts.join(" ");
}

function simplifyCaseStage(commandCenterStage, hsaStage) {
  const cc = String(commandCenterStage ?? "").toLowerCase();
  const hs = String(hsaStage ?? "").toLowerCase();

  if (cc.includes("claim") || hs.includes("claim") || cc.includes("discharge"))
    return "Claim";

  if (cc.includes("enhancement") || hs.includes("enhancement"))
    return "Enhancement";

  if (cc.includes("admission") || hs.includes("admission")) return "Admission";

  return "-";
}

export default function DoctorReportDownloadButton({
  selectedDoctorId,
  timeframe,
  chartDateSource,
  customRangeStart,
  customRangeEnd,
}) {
  const [loading, setLoading] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  const isSingleCustomDay =
    chartDateSource === "custom" &&
    customRangeStart &&
    customRangeEnd &&
    customRangeStart === customRangeEnd;

  const canDownload =
    selectedDoctorId && (timeframe === "Today" || isSingleCustomDay);
  const getDateRange = () => {
    let rawStartDate;
    let rawEndDate;

    if (chartDateSource === "custom" && customRangeStart && customRangeEnd) {
      rawStartDate = `${customRangeStart}T00:00:00.000`;
      rawEndDate = `${customRangeEnd}T23:59:59.999`;
    } else {
      const range = getChartDateRangeForTimeframe("Today");

      rawStartDate = range.startDate;
      rawEndDate = range.endDate;
    }

    return {
      startDate: rawStartDate?.split("+")[0],
      endDate: rawEndDate?.split("+")[0],
    };
  };

  const fetchAllPages = async () => {
    const { startDate, endDate } = getDateRange();

    // First call → get total rows
    const firstRes = await ReportServices.getCcDoctorProcessedStageList({
      page: 1,
      limit: PAGE_LIMIT,
      doctorId: selectedDoctorId,
      startDate,
      endDate,
    });

    const firstData = firstRes?.data || {};

    const total = firstData.total || 0;

    if (!total) return [];

    // Second call → fetch everything
    const fullRes = await ReportServices.getCcDoctorProcessedStageList({
      page: 1,
      limit: total,
      doctorId: selectedDoctorId,
      startDate,
      endDate,
    });

    return fullRes?.data?.processedStages || [];
  };

  const generatePdf = async (rows) => {
    const doc = new jsPDF("l", "mm", "a4");

    // Fetch users map for doctor names
    const usersRes = await ReportServices.getUsers();
    const usersMap = usersRes?.data?.users || {};

    const tableRows = rows.map((item, index) => {
      const c = item.case || {};
      const p = c.patient || {};
      const h = c.hospital || {};

      const start = formatGridDateTime(item.assginDate);
      const end = formatGridDateTime(item.updatedAt);

      const startDate = new Date(item.assginDate);
      const endDate = new Date(item.updatedAt);

      const tatSeconds = Math.floor((endDate - startDate) / 1000);

      const tat = formatDurationSeconds(tatSeconds);

      const procedures = (c.caseprocedures || [])
        .map((proc, i) => `${i + 1}. ${proc.procedure}`)
        .join("\n\n");

      const stage = simplifyCaseStage(item.commandCenterStage, item.hsaStage);

      const assignedDoctor =
        usersMap[item.assignedTo] || item.assignedDoctor?.fullName || "-";

      return [
        index + 1,
        p.patientName || "-",
        h.name || "-",
        c.pmjayRegistrationId || "-",
        stage,
        c.caseType || "-",
        formatGridDateTime(item.createdAt),
        start,
        end,
        tat,
        assignedDoctor,
        item.performedTask?.commandCenterStage || "-",
        procedures || "-",
        item.commandCenterStageText || "-",
      ];
    });

    autoTable(doc, {
      head: [
        [
          "No",
          "Patient",
          "Hospital",
          "Claim ID",
          "Case Stage",
          "Case Type",
          "Due",
          "Start",
          "End",
          "TAT",
          "Assigned",
          "Action",
          "Procedure",
          "Status",
        ],
      ],
      body: tableRows,
      styles: {
        fontSize: 7,
        cellPadding: 2,
        valign: "middle",
      },
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 12 }, // wider No column
      },
    });

    doc.save("doctor-performance-report.pdf");
  };

  const generateExcel = async (rows) => {
    if (!rows?.length) {
      return;
    }

    const usersRes = await ReportServices.getUsers();
    const usersMap = usersRes?.data?.users || {};

    const excelData = rows.map((item, index) => {
      const c = item?.case || {};
      const p = c?.patient || {};
      const h = c?.hospital || {};

      const startDate = item?.assginDate ? new Date(item.assginDate) : null;

      const endDate = item?.updatedAt ? new Date(item.updatedAt) : null;

      const tatSeconds =
        startDate && endDate ? Math.floor((endDate - startDate) / 1000) : 0;

      const procedures = (c?.caseprocedures || [])
        .map((proc, i) => `${i + 1}. ${proc?.procedure || "-"}`)
        .join("\n");

      const stage = simplifyCaseStage(item?.commandCenterStage, item?.hsaStage);

      const assignedDoctor =
        usersMap[item?.assignedTo] || item?.assignedDoctor?.fullName || "-";

      return {
        No: index + 1,
        Patient: p?.patientName || "-",
        Hospital: h?.name || "-",
        "Claim ID": c?.pmjayRegistrationId || "-",
        "Case Stage": stage,
        "Case Type": c?.caseType || "-",
        Due: formatGridDateTime(item?.createdAt).replace("\n", " "),
        Start: formatGridDateTime(item?.assginDate).replace("\n", " "),
        End: formatGridDateTime(item?.updatedAt).replace("\n", " "),
        TAT: formatDurationSeconds(tatSeconds),
        Assigned: assignedDoctor,
        Action: item?.performedTask?.commandCenterStage || "-",
        Procedure: procedures || "-",
        Status: item?.commandCenterStageText || "-",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 25 },
      { wch: 28 },
      { wch: 22 },
      { wch: 18 },
      { wch: 18 },
      { wch: 22 },
      { wch: 22 },
      { wch: 22 },
      { wch: 12 },
      { wch: 25 },
      { wch: 25 },
      { wch: 40 },
      { wch: 30 },
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Doctor Performance Report",
    );

    XLSX.writeFile(workbook, "doctor-performance-report.xlsx");
  };

  const handlePdfDownload = async () => {
    if (!canDownload) return;

    setShowDownloadMenu(false);
    setLoading(true);

    try {
      const rows = await fetchAllPages();

      await generatePdf(rows);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleExcelDownload = async () => {
    if (!canDownload) return;

    setShowDownloadMenu(false);
    setLoading(true);

    try {
      const rows = await fetchAllPages();

      await generateExcel(rows);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!canDownload) return;

    setLoading(true);

    try {
      const rows = await fetchAllPages();

      await generatePdf(rows);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative shrink-0">
      {/* Download Button */}
      <button
        type="button"
        disabled={!canDownload || loading}
        onClick={() => {
          if (!loading && canDownload) {
            setShowDownloadMenu((prev) => !prev);
          }
        }}
        className="flex items-center justify-center gap-[10px] w-[140px] h-[42px] px-[18px] py-[17px] rounded-[26px] bg-white border-none cursor-pointer text-gray-900 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <span className="font-lufga font-medium text-[12px] leading-[12px] tracking-[-0.5px] whitespace-nowrap">
            Downloading...
          </span>
        ) : (
          <>
            <img
              src={downloadIcon}
              alt="download"
              className="w-[14px] h-[14px] object-contain"
            />

            <span className="font-lufga font-medium text-[12px] leading-[12px] tracking-[-0.5px] whitespace-nowrap">
              Download Report
            </span>

            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform duration-200 ${
                showDownloadMenu ? "rotate-180" : ""
              }`}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </>
        )}
      </button>

      {/* Download Dropdown */}
      {showDownloadMenu && (
        <div className="absolute right-0 top-full mt-0 w-full min-w-[140px] bg-white border border-[#AFAFAF] rounded-b-[10px] rounded-t-none overflow-hidden z-50 shadow-sm">
          <button
            type="button"
            disabled={loading}
            onClick={handlePdfDownload}
            className="w-full text-left px-4 py-3 text-sm text-[#191919] bg-white hover:bg-[#F8F8F8] border-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            Download PDF
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={handleExcelDownload}
            className="w-full text-left px-4 py-3 text-sm text-[#191919] bg-white hover:bg-[#F8F8F8] border-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            Download Excel
          </button>
        </div>
      )}
    </div>
  );
}
